import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { MoreVertical, Link, Pencil, Trash2, Pin, Bookmark } from "lucide-react";
import { trackShareClick } from '../utils/track';
import SaveCreditLine from './SaveCreditLine';

const RecipeCard = ({ recipe, isAdmin, onEdit, onDelete, onPin, isPublic, userId, username, onClickInterceptor, onBeforeNavigate, onSave, isSaved, showPinnedShortcut = false }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Determine the link URL. Always prefer the pretty /username/slug link —
  // even on the owner's private view — so the address bar goes straight to
  // its final URL instead of landing on /recipe/:id and visibly swapping.
  const getRecipeUrl = () => {
    if (username && recipe.slug) {
      return `/${username}/${recipe.slug}`;
    }
    if (isPublic && userId) {
      return `/${userId}/${recipe.id}`;
    }
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.username && recipe.slug) {
      return `/${user.username}/${recipe.slug}`;
    }
    return `/recipe/${recipe.id}`;
  };

  const handleCopyLink = (e) => {
    e.stopPropagation();
    // Build the clean URL for this recipe
    const baseUrl = window.location.origin;
    let link;
    
    if (username && recipe.slug) {
      link = `${baseUrl}/${username}/${recipe.slug}`;
    } else if (isPublic && userId) {
      link = `${baseUrl}/${userId}/${recipe.id}`;
    } else {
      // For private view, get from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.username && recipe.slug) {
        link = `${baseUrl}/${user.username}/${recipe.slug}`;
      } else {
        link = `${baseUrl}/${user.id}/${recipe.id}`;
      }
      trackShareClick();
    }
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard!");
  };

  const handleCardClick = (e) => {
    if (e.defaultPrevented) return;
    if (onClickInterceptor && !onClickInterceptor()) return;
    // Let the list page remember its category/scroll before we leave it
    if (onBeforeNavigate) onBeforeNavigate();
    navigate(getRecipeUrl());
  };

  const handlePin = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setMenuOpen(false); // Close menu immediately
    if (onPin) {
      onPin(recipe.id);
    }
  };

  return (
    <Card 
      className="cursor-pointer group transition-all duration-200 hover:border-[#A0988A]"
      style={{ backgroundColor: '#F3EEE4', boxShadow: 'none', border: '1px solid #D6D0C2' }}
      onClick={handleCardClick}
      data-testid={`recipe-card-${recipe.id}`}
    >
      {recipe.image_url && (
        <div style={{ 
          width: '100%',
          height: '160px',
          overflow: 'hidden',
          flexShrink: 0,
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px'
        }}>
          <img 
            src={recipe.image_url} 
            alt={recipe.title}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block'
            }}
          />
        </div>
      )}
      <CardHeader>
        <div className={`flex justify-between ${isPublic ? 'items-end' : 'items-start'}`}>
          <div className="flex-1 min-w-0" data-testid={`recipe-card-content-${recipe.id}`}>
            <CardTitle className="text-3xl mb-3" style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }} data-testid={`recipe-title-${recipe.id}`}>{recipe.title}</CardTitle>
            {recipe.categories && recipe.categories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {recipe.categories.map((cat, index) => (
                  <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#C9D7E9', color: '#0F2444' }} data-testid={`recipe-category-${recipe.id}-${index}`}>
                    {cat}
                  </span>
                ))}
              </div>
            )}
            {/* Lead with where it was saved from; the original author follows */}
            <SaveCreditLine
              recipe={recipe}
              placement="card"
            />
            {recipe.creator && !(recipe.saved_from_user_id && recipe.saved_from_name) && (
              <p
                className="text-sm mt-2"
                style={{
                  color: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  maxWidth: isPublic ? '75%' : '100%',
                  overflowWrap: 'break-word',
                }}
                data-testid={`recipe-creator-${recipe.id}`}
              >
                by {recipe.creator}
              </p>
            )}
          </div>
          
          {/* Show save + link icon buttons for public view, bottom-aligned with
              the last line of card text (creator if present, else categories) */}
          {isPublic && (
            <div className="flex items-end gap-1 ml-2" style={{ flexShrink: 0 }}>
              {onSave && (
                <button
                  onClick={(e) => { e.stopPropagation(); onSave(recipe); }}
                  aria-label={isSaved ? "Saved to your Wellby" : "Save to your Wellby"}
                  className="flex items-center justify-center save-icon-btn focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#51120A] rounded"
                  style={{
                    width: '26px',
                    height: '28px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    padding: 0,
                    flexShrink: 0
                  }}
                  title={isSaved ? "Saved to your Wellby" : "Save to your Wellby"}
                  data-testid={`save-recipe-btn-${recipe.id}`}
                >
                  {/* No hover container: hovering fills the bookmark itself blush */}
                  <Bookmark
                    className={`h-[26px] w-[26px] transition-colors ${isSaved ? 'fill-[#51120A]' : 'fill-none'}`}
                    strokeWidth={1.4}
                    style={{ color: '#51120A' }}
                  />
                </button>
              )}
              <button
                onClick={handleCopyLink}
                aria-label="Copy link to this recipe"
                className="flex items-center justify-center copylink-btn focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#51120A] rounded"
                style={{
                  width: '26px',
                  height: '28px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  padding: 0,
                  flexShrink: 0
                }}
                title="Copy Link"
              >
                {/* The existing ring itself fills blush on hover — no extra circle.
                    Sizes are the bookmark's +20% pair: 26px ring, 16px link. */}
                <span
                  className="copylink-ring flex items-center justify-center transition-colors"
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    border: '1.5px solid #51120A',
                    flexShrink: 0
                  }}
                >
                  <Link className="h-4 w-4" strokeWidth={2.25} style={{ color: '#51120A' }} />
                </span>
              </button>
            </div>
          )}
          
          {/* Show 3-dot menu for admin view */}
          {isAdmin && (
            <div className="flex flex-col items-end justify-between self-stretch">
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-8 w-8 p-0 rounded-full hover-quiet"
                  data-testid={`recipe-menu-btn-${recipe.id}`}
                >
                  <MoreVertical className="h-4 w-4" style={{ color: '#51120A' }} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="shadow-none" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); navigate(`/recipe/${recipe.id}?edit=true`); }}
                  data-testid={`edit-recipe-menu-${recipe.id}`}
                  className="hover:bg-[#EEE7D8] focus:bg-[#EEE7D8] flex items-center gap-2"
                  style={{ color: '#875C66', fontFamily: "'Bricolage Grotesque', sans-serif", cursor: 'pointer' }}
                >
                  <Pencil size={14} />
                  Edit
                </DropdownMenuItem>
                {recipe.is_public === true ? (
                  <DropdownMenuItem
                    onClick={(e) => { handleCopyLink(e); setMenuOpen(false); }}
                    data-testid={`share-recipe-menu-${recipe.id}`}
                    className="hover:bg-[#EEE7D8] focus:bg-[#EEE7D8] flex items-center gap-2"
                    style={{ color: '#875C66', fontFamily: "'Bricolage Grotesque', sans-serif", cursor: 'pointer' }}
                  >
                    <Link size={14} />
                    Copy Link
                  </DropdownMenuItem>
                ) : (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem
                          onSelect={(e) => e.preventDefault()}
                          onClick={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.preventDefault()}
                          data-testid={`share-recipe-menu-${recipe.id}`}
                          className="flex items-center gap-2 opacity-50"
                          style={{ color: '#875C66', fontFamily: "'Bricolage Grotesque', sans-serif", cursor: 'not-allowed' }}
                        >
                          <Link size={14} />
                          Copy Link
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent side="left" sideOffset={8} className="bg-[#51120A] text-white">
                        Make recipe public in Edit to share
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <DropdownMenuItem
                  onClick={handlePin}
                  data-testid={`pin-recipe-menu-${recipe.id}`}
                  className="hover:bg-[#EEE7D8] focus:bg-[#EEE7D8] flex items-center gap-2"
                  style={{ color: '#875C66', fontFamily: "'Bricolage Grotesque', sans-serif", cursor: 'pointer' }}
                >
                  <Pin size={14} style={{ fill: recipe.pinned ? '#875C66' : 'none' }} />
                  {recipe.pinned ? 'Unpin' : 'Pin'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(recipe.id); }}
                  data-testid={`delete-recipe-menu-${recipe.id}`}
                  className="hover:bg-[#EEE7D8] focus:bg-[#EEE7D8] flex items-center gap-2"
                  style={{ color: '#875C66', fontFamily: "'Bricolage Grotesque', sans-serif", cursor: 'pointer' }}
                >
                  <Trash2 size={14} />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {showPinnedShortcut && recipe.pinned && (
              <button
                type="button"
                onClick={handlePin}
                aria-label="Unpin recipe"
                title="Unpin recipe"
                className="-mr-1.5 -mb-1.5 mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded transition-opacity hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#51120A]"
                style={{ color: '#51120A', backgroundColor: 'transparent', border: 'none' }}
                data-testid={`unpin-recipe-btn-${recipe.id}`}
              >
                <Pin size={18} fill="currentColor" aria-hidden="true" />
              </button>
            )}
            </div>
          )}
        </div>
      </CardHeader>
    </Card>
  );
};

export default RecipeCard;
